<template>
  <div class="widget">
    <div class="widget-header">
      <h3 class="mb-0">
        {{ isCreating ? $t("firewall.create_firewall") : firewall.name }}
      </h3>
    </div>
    <div class="alert alert-warning mb-0" role="alert">
      {{ $t("firewall.business_rule_alert") }}
    </div>

    <div class="widget-body" style="overflow-x: auto">
      <div class="mb-5" v-if="isCreating">
        <div class="d-flex" style="align-items: center">
          <h3 class="mb-0" style="flex-grow: 1">
            {{ $t("firewall.firewall_name") }}
          </h3>
        </div>
        <div class="my-4">
          <input v-model="name" class="border form-control" />
        </div>
      </div>

      <div class="mb-5">
        <div class="d-flex mb-4" style="align-items: center">
          <div style="flex-grow: 1; display: flex">
            <h3 class="mb-0">{{ $t("firewall.inboundRules") }}</h3>
            <i
              class="fa fa-info"
              :data-tooltip-text="$t('firewall.inboundRulesInfo')"
            ></i>
          </div>
        </div>
        <FirewallRulesTable
          :sources="'in'"
          :rules="rulesIn"
          :rulesMacros="rulesMacros"
          :ruleProtocols="ruleProtocols"
          :isCreating="isCreating"
          @removeRule="removeRule"
          @cancel="cancel"
          @save="save"
        ></FirewallRulesTable>
        <div class="d-flex mb-4" style="align-items: center">
          <div style="flex-grow: 1; display: flex">
          </div>

          <div class="form-group dropdown m-0" v-dropdown.closeOnMenuClick>
            <button
              id="addNewRuleIn"
              class="btn btn-primary btn-sm dropdown-toggle"
              type="button"
              data-toggle="dropdown"
            >
              {{ $t("firewall.addRule") }}
            </button>
            <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
              <div class="dropdown-menu-content">
                <a
                  class="dropdown-item"
                  v-for="(option, index) in rulesMacros"
                  :key="index"
                  @click="addRule('in', option)"
                  >{{ option.macro }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-5">
        <div class="d-flex mb-4" style="align-items: center">
          <div style="flex-grow: 1; display: flex">
            <h3 class="mb-0">{{ $t("firewall.outboundRules") }}</h3>
            <i
              class="fa fa-info"
              :data-tooltip-text="$t('firewall.outboundRulesInfo')"
            ></i>
          </div>
        </div>
        <FirewallRulesTable
          :sources="'out'"
          :rules="rulesOut"
          :rulesMacros="rulesMacros"
          :ruleProtocols="ruleProtocols"
          :isCreating="isCreating"
          @removeRule="removeRule"
          @cancel="cancel"
          @save="save"
        ></FirewallRulesTable>
        <div class="d-flex mb-4" style="align-items: center">
          <div style="flex-grow: 1; display: flex">
          </div>

          <div class="form-group dropdown m-0" v-dropdown.closeOnMenuClick>
            <button
              id="addNewRuleOut"
              class="btn btn-primary btn-sm dropdown-toggle"
              type="button"
              data-toggle="dropdown"
            >
              {{ $t("firewall.addRule") }}
            </button>
            <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
              <div class="dropdown-menu-content">
                <a
                  class="dropdown-item"
                  v-for="(option, index) in rulesMacros"
                  :key="index"
                  @click="addRule('out', option)"
                  >{{ option.macro }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-5">
        <div class="d-flex" style="align-items: center">
          <h3 class="mb-0" style="flex-grow: 1">
            {{ $t("firewall.add_vms") }}
          </h3>
        </div>
        <div class="m-4">

          <multiselect
            v-model="resources"
            :placeholder="$t('firewall.search_vms_or_tags')"
            label="name"
            track-by="name"
            :options="userVms !== null ? userVms : []"
            :multiple="true"
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="option__desc">
                <span class="option__title">{{ props.option.name }}</span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">{{ props.option.name }}</span>
              </div>
            </template>
          </multiselect>
          <div style="max-width: 450px">
            <div class="spinner spinner-md" v-if="is_typing"></div>
          </div>
        </div>
      </div>

      <div v-if="isCreating">
        <button
          id="createFirewall"
          class="btn btn-primary btn-sm"
          @click="createFirewall()"
          :disabled="firewallInProgress"
        >
          {{ $t("firewall.create_firewall") }}
        </button>
      </div>

      <div v-else>
        <button
          id="createFirewall"
          class="btn btn-primary btn-sm"
          @click="updateFirewall()"
          :disabled="inProgress"
        >
          {{ $t("firewall.update_firewall") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAxios } from "services/ajax";
import SourceTags from "./SourceTags";
import FirewallRulesTable from "./FirewallRulesTable";
import VueTagsInput from "@johmun/vue-tags-input";
import { debounce } from "lodash";
import store from "../../store";

export default {
  components: {
    SourceTags,
    FirewallRulesTable,
    VueTagsInput,
  },

  computed: {
    rulesIn() {
      return this.rules.filter((rule) => rule.type === "in");
    },
    rulesOut() {
      return this.rules.filter((rule) => rule.type === "out");
    },
    isCreating() {
      return this.$route.params.firewall === "new";
    },
    firewallInProgress() {
      return this.firewall.in_progress;
    },
    inProgress() {
      return this.in_progress;
    },
  },

  watch: {
    search: function (newValue, old) {
      console.table(newValue, old);
      if (newValue !== old) {
        this.is_typing = true;
      }
    },
  },

  data() {
    return {
      name: "",
      rules: [],
      in_progress: false,
      firewall: { in_progress: false },
      searchForVmsOrTags: null,
      search: "",
      is_typing: false,
      autocompleteItems: [],
      resources: [],
      rulesMacros: [
        {
          macro: "HTTP",
          dport: "80",
          proto: "TCP",
          source: '',
        },
        {
          macro: "HTTPS",
          dport: "443",
          proto: "TCP",
          source: '',
        },
        {
          macro: "FTP",
          dport: "21",
          proto: "TCP",
          source: '',
        },
        {
          macro: "SSH",
          dport: "22",
          proto: "TCP",
          source: '',
        },
        {
          macro: "DNS",
          dport: "55",
          proto: "UDP",
          source: '',
        },
        {
          macro: "MSSQL",
          dport: "1433",
          proto: "TCP",
          source: '',
        },
        {
          macro: "MYSQL",
          dport: "3306",
          proto: "TCP",
          source: '',
        },
        {
          macro: "RDP",
          dport: "3389",
          proto: "TCP",
          source: '',
        },
        {
          macro: "Custom",
          dport: "9090",
          proto: "TCP",
          source: '',
        },
      ],
      userVms: [],
      selectUserVms: [],
      isLoadVms: false,
      ruleProtocols: ["TCP", "UDP", "ICMP"],
    };
  },
  mounted() {
    if (this.$route.params.firewall !== "new") {
      this.getFirewall(this.$route.params.firewall);
    } else {
      this.addRule(
        "in",
        this.rulesMacros.find((r) => r.macro === "SSH")
      );
      this.addRule(
        "in",
        this.rulesMacros.find((r) => r.macro === "Custom")
      );
      this.addRule(
        "in",
        {
          macro: "Custom",
          dport: "",
          proto: "ICMP",
          source: '',
        }
      );
      this.addRule(
        "out",
        {
          macro: "Custom",
          dport: "",
          proto: "TCP",
          source: '',
        }
      );
      this.addRule(
        "out",
        {
          macro: "Custom",
          dport: "",
          proto: "UDP",
          source: '',
        }
      );
      this.addRule(
        "out",
        {
          macro: "Custom",
          dport: "",
          proto: "ICMP",
          source: '',
        }
      );
    }

    this.$watch(
      "search",
      debounce(() => {
        if (this.search) {
          getAxios()
            .get(`/configuration/firewall/search-vms/${this.search}`)
            .then((response) => {
              this.autocompleteItems = response.data.map((r) => {
                return {
                  text: r.hostname.hostname,
                  id: r.id,
                  type: r.type ? r.type : "",
                };
              });

              this.is_typing = false;
            })
            .finally((res) => {
              this.is_typing = false;
            });
        } else {
          this.is_typing = false;
        }
      }, 500)
    );

    this.collectVmList();
  },
  methods: {
    customLabel({ display_name }) {
      return `${display_name}`;
    },
    addTag (newTag) {
      const tag = {
        name: newTag,
        id: newTag.substring(0, 2) + Math.floor((Math.random() * 10000000))
      }

      this.userVms.push(tag)
      this.selectUserVms.push(tag)
    },
    collectVmList() {
      this.isLoadVms = true;
      this.userVms = [];

      getAxios()
        .get("configuration/firewall/list-vms")
        .then((response) => {
          this.userVms = response.data.FirewallPanelController.data.map(
            (item) => {
              const ipAddresses = item.vm_nics.map((nic) => nic.ip.ip_address);
              const formattedString = `${item.name} (${ipAddresses.join(
                ", "
              )})`;
              return {
                name: formattedString,
                id: item.id,
                type: "TAG",
                tiClasses: [ "ti-valid" ] 
              };
            }
          );
        })
        .catch(function () {})
        .finally(() => {
          this.isLoadVms = false;
        });
    },
    cancel(rule) {
      if (!rule.id) {
        this.rules.splice(
          this.rules.findIndex((r) => r === rule),
          1
        );
      } else if (rule.original) {
        this.rules.splice(
          this.rules.findIndex((r) => r === rule),
          1,
          rule.original
        );
      }
    },
    getFirewall(id) {
      getAxios()
        .get(`/firewall/${this.$route.params.firewall}`)
        .then((response) => {
          this.firewall = response.data;
          this.rules = response.data.firewall_rules;

          this.resources = response.data.firewall_vms.map(
            (item) => {
              const ipAddresses = item.vm_nics.map((nic) => nic.ip.ip_address);
              const formattedString = `${item.name} (${ipAddresses.join(
                ", "
              )})`;
              return {
                name: formattedString,
                id: item.id,
                type: "TAG",
                tiClasses: [ "ti-valid" ] 
              };
            }
          );
        })
        .catch((err) => {
          if (err) {
            console.log(err);
          }
        });
    },
    removeRule(rule) {
      // validation
      if (this.rules.length <= 1) {
        this.showToast(this.$t("firewall.no_rules"), {
          icon: "fa-remove",
          position: "top-center",
          duration: 3000,
          fullWidth: false,
        });
        return;
      }

      this.rules.forEach((r) => {
        if (r) this.$set(r, "in_progress", true);
      });

      getAxios()
        .delete(`/configuration/firewall/${rule.firewall_id}/rules/${rule.pos}`)
        .then((response) => {
          // Refresh rules
          this.getFirewall(this.$route.params.firewall);

          this.showToast(this.$t("firewall.delete_rule_success"), {
            icon: "fa-remove",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });
        })
        .catch((err) => {
          if (err) {
            console.log(err);
            this.showToast(err, {
              icon: "fa-remove",
              position: "top-center",
              duration: 5000,
              fullWidth: false,
            });
          }
        })
        .finally(() => {
          this.rules.forEach((r) => {
            if (r) this.$set(r, "in_progress", false);
          });
        });
    },
    addRule(type, data) {
      this.rules.push({
        type: type,
        ...data,
      });
    },
    save(rule) {
      let method = "post";
      if (rule.id) {
        method = "put";
      }

      this.rules.forEach((r) => {
        if (r) this.$set(r, "in_progress", true);
      });

      getAxios()
        [method](
          `/configuration/firewall/${this.$route.params.firewall}/rule${
            rule.id ? "/" + rule.id : ""
          }`,
          {
            ...rule,
            macro: rule.macro === "Custom" ? '' : rule.macro,
          }
        )
        .then((response) => {
          // refresh rules
          this.getFirewall(this.$route.params.firewall);

          const text =
            method == "post"
              ? this.$t("firewall.save_rule_success")
              : this.$t("firewall.update_rule_success");

          this.showToast(text, {
            icon: "fa-save",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });
        })
        .catch((err) => {
          this.rules.forEach((r) => {
            if (r) this.$set(r, "in_progress", false);
          });
          if (err) {
            console.log(err);
          }
        });
    },
    createFirewall() {
      let payload = {
        name: this.name,
        rules: this.rules,
        resources: this.resources,
      };

      // validation
      if (this.rules.length < 1) {
        this.showToast(this.$t("firewall.no_rules"), {
          icon: "fa-remove",
          position: "top-center",
          duration: 3000,
          fullWidth: false,
        });
        return;
      }
      if (
        this.resources.length < 1 ||
        this.resources.some((item) => !item.id)
      ) {
        this.showToast(this.$t("firewall.no_resources"), {
          icon: "fa-remove",
          position: "top-center",
          duration: 3000,
          fullWidth: false,
        });
        this.resources = this.resources.filter((item) => item.id);
        return;
      }
      if (this.name.length < 1) {
        this.showToast(this.$t("firewall.no_name"), {
          icon: "fa-remove",
          position: "top-center",
          duration: 3000,
          fullWidth: false,
        });
        return;
      }

      this.firewall.in_progress = true;
      payload.rules = payload.rules.map((rule) => {
        return {
          ...rule,
          macro: rule.macro === "Custom" ? '' : rule.macro,
        };
      });
      store.commit('setLoading', true)
      getAxios()
        .post(`/configuration/firewall/save`, payload)
        .then((response) => {
          this.firewall.in_progress = false;
          this.showToast(this.$t("firewall.create_firewall_succeeded"), {
            icon: "fa-save",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });

          this.$router.push(`/firewalls/${response.data.id}`);
          this.getFirewall(response.data.id);
        })
        .catch((err) => {
          if (err) {
            console.log(err);
          }
        })
        .finally(() => {
          store.commit('setLoading', false)
          this.firewall.in_progress = false;
        });
    },
    updateFirewall() {
      // validation
      if (
        this.resources.length < 1 ||
        this.resources.some((item) => !item.id)
      ) {
        this.showToast(this.$t("firewall.no_resources"), {
          icon: "fa-remove",
          position: "top-center",
          duration: 3000,
          fullWidth: false,
        });
        this.resources = this.resources.filter((item) => item.id);
        return;
      }

      let payload = {
        resources: this.resources,
      };
      this.in_progress = true;
      getAxios()
        .put(`/configuration/firewall/${this.$route.params.firewall}`, payload)
        .then((response) => {
          this.resources = response.data.firewall_vms.map((el) => {
            return {
              text: el.vm.hostname.hostname,
              id: el.vm.id,
              type: "vm",
            };
          });

          this.showToast(this.$t("firewall.update_firewall_succeeded"), {
            icon: "fa-save",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });
        })
        .catch((err) => {
          if (err) {
            console.log(err);
          }
        })
        .finally(() => {
          this.in_progress = false;
        });
    },
    tagsChanged(tags) {
      this.resources = tags;
    },
    beforeAddingSource({ tag, addTag }) {
      if (!tag.type) {
        tag.type = "TAG";
      }
      addTag();
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>
<style lang="scss" scoped>
.spinner {
  height: 40px;
  width: 40px;
  margin: 0 auto;
  position: relative;
  -webkit-animation: rotation 0.6s infinite linear;
  -moz-animation: rotation 0.6s infinite linear;
  -o-animation: rotation 0.6s infinite linear;
  animation: rotation 0.6s infinite linear;
  border: 6px solid rgba(0, 174, 239, 0.15);
  border-radius: 100%;
}

.spinner:before {
  content: "";
  display: block;
  position: absolute;
  left: -6px;
  top: -6px;
  height: 100%;
  width: 100%;
  border-top: 6px solid rgba(0, 174, 239, 0.8);
  border-left: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid transparent;
  border-radius: 100%;
}

@-webkit-keyframes rotation {
  from {
    -webkit-transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(359deg);
  }
}
@-moz-keyframes rotation {
  from {
    -moz-transform: rotate(0deg);
  }
  to {
    -moz-transform: rotate(359deg);
  }
}
@-o-keyframes rotation {
  from {
    -o-transform: rotate(0deg);
  }
  to {
    -o-transform: rotate(359deg);
  }
}
@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

/* tooltip */
[data-tooltip-text]:hover {
  position: relative;
}

[data-tooltip-text]:hover:after {
  background-color: #000000;
  background-color: rgba(0, 0, 0, 0.8);

  -webkit-box-shadow: 0px 0px 3px 1px rgba(50, 50, 50, 0.4);
  -moz-box-shadow: 0px 0px 3px 1px rgba(50, 50, 50, 0.4);
  box-shadow: 0px 0px 3px 1px rgba(50, 50, 50, 0.4);

  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;

  color: #ffffff;
  font-size: 12px;
  content: attr(data-tooltip-text);

  margin-bottom: 10px;
  top: 130%;
  left: 0;
  padding: 7px 12px;
  position: absolute;
  width: auto;
  min-width: 300px;
  max-width: 600px;
  word-wrap: break-word;

  z-index: 9999;
}

.fa-info {
  border: 1px solid;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  padding: 1px;
  margin-left: 5px;
}

.fa-info:before {
  margin-left: 5px;
}

a:not([href]):not([tabindex]) {
  color: #000 !important;
  text-decoration: none;
}
</style>


// WEBPACK FOOTER //
// src/components/firewalls/Firewall.vue